import posthog from "posthog-js";
import { Component, ErrorInfo, ReactNode } from "react";
import ErrorPage from "./ErrorPage";

/**
 * React render errors never reach window.onerror, so exception autocapture
 * (capture_exceptions in analytics/posthog.ts) misses them — report them here.
 * Global errors and unhandled rejections are autocaptured; don't duplicate those.
 */
function captureExceptionToPostHog(
  error: unknown,
  properties: Record<string, unknown>
): void {
  if (typeof posthog?.captureException === "function") {
    posthog.captureException(error, properties);
  }
}

function safeString(value: unknown, maxLen = 2000): string {
  if (value == null) return "";
  const s = typeof value === "string" ? value : String(value);
  return s.length > maxLen ? s.slice(0, maxLen) + "…" : s;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  showDebugInfo?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  globalError?: {
    message: string;
    source: string;
    lineno: number;
    colno: number;
    error: Error | null;
  };
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };

    // Bind the error handling methods to the class instance
    this.handleGlobalError = this.handleGlobalError.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);

    // Attach global error listeners
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });
    console.error("React Error caught by ErrorBoundary:", error, errorInfo);
    captureExceptionToPostHog(error, {
      error_source: "react_error_boundary",
      component_stack: safeString(errorInfo?.componentStack)
    });
  }

  componentWillUnmount() {
    // Remove global error listeners
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  handleGlobalError(event: ErrorEvent) {
    this.setState({
      hasError: true,
      globalError: {
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      }
    });
    console.error(
      "Global Error:",
      event.message,
      event.filename,
      event.lineno,
      event.colno,
      event.error
    );
    // PostHog autocaptures window.onerror as $exception.
  }

  handleUnhandledRejection(event: PromiseRejectionEvent) {
    this.setState({
      hasError: true,
      globalError: {
        message:
          event.reason instanceof Error
            ? event.reason.message
            : "Unhandled Promise Rejection",
        source: "",
        lineno: 0,
        colno: 0,
        error: event.reason instanceof Error ? event.reason : null
      }
    });
    console.error("Unhandled Rejection:", event.reason);
    // PostHog autocaptures unhandled rejections as $exception.
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          globalError={this.state.globalError}
          showDebugInfo={this.props.showDebugInfo}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
